import React, { useEffect, useState, useRef } from 'react';
import {
    Link as RouterLink
} from 'react-router-dom';
import { Auth } from 'aws-amplify';
import Cookies from 'universal-cookie';
import {
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Divider,
    Hidden,
    Grid,
    IconButton,
    Link,
    TextField,
    Toolbar,
    Tooltip,
    Typography
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { DataGrid } from '@material-ui/data-grid';
import {
    Map,
    PersonAdd,
    Place
} from '@material-ui/icons';
import NavigationIcon from '@material-ui/icons/Navigation';
import { CSVLink } from "react-csv";
import jsPDF from "jspdf";
import 'jspdf-autotable';
import { GoogleMap } from '../Components/GoogleMap.jsx';
import TlmBreadcrumbs from '../Components/TlmBreadcrumbs.jsx';
import TlmFilterBar from '../Components/TlmFilterBar.jsx';
import { host, status_severity, srcFilter, FieldSelectLabel, COOKIE_NAMES, useCookiesForData } from "../../data";
import NavigationButton from '../Components/NavigationButton.js';
import { isValidEmail } from '../../helpers/isValidEmail';
import { roundToDecimals } from '../../helpers/roundToDecimals.js';
import { useTranslation } from 'react-i18next';
import { userSettingsData } from '../../services/userSettingsData';

const useStyles = makeStyles((theme) => ({
    grow: {
        flexGrow: 1,
    },
    grow_2x: {
        flexGrow: 55,
    },
    iconButtons: {
        marginRight: theme.spacing(1),
    },
    topMap: {
        width: '100%',
        height: '33vh',
    },
    link: {
        color: theme.palette.info.dark,
    },
    table: {
        width: '100%',
        height: 'calc(100vh - 64px - 32px - 65px - 64px)',
        [theme.breakpoints.down('sm')]: {
            height: 'calc(40vh - 64px - 32px)'
        }
    },
    table_noMap: {
        width: '100%',
        height: 'calc(100vh - 64px - 32px - 65px - 64px)',
        [theme.breakpoints.down('sm')]: {
            height: 'calc(100vh - 64px - 32px - 65px - 64px - 64px - 32px)'
        }
    },
    ok: {
        minWidth: '64px',
        backgroundColor: theme.palette.success.main,
        color: theme.palette.primary.contrastText,
    },
    low: {
        minWidth: '64px',
        backgroundColor: theme.palette.warning.main,
        color: theme.palette.primary.contrastText,
    },
    crit: {
        minWidth: '64px',
        backgroundColor: theme.palette.error.main,
        color: theme.palette.primary.contrastText,
    },
    error: {
        minWidth: '64px',
        backgroundColor: theme.palette.error.dark,
        color: theme.palette.primary.contrastText,
    },
    statusNone: {
        minWidth: '64px',
        backgroundColor: theme.palette.primary.light,
        color: theme.palette.primary.contrastText,
    },
    HeaderIcon: {
        marginLeft: '5px'
    },
    container: {
        display: 'flex',
        justifyContent: 'space-around',
        padding: theme.spacing(2),
        [theme.breakpoints.down('xs')]: {
            flexWrap: 'wrap',
        }
    },
    wrap: {
        display: 'flex',
        [theme.breakpoints.down('xs')]: {
            width: '100%',
        }
    }
}));

// Branch page component. Display the branch view.
export default function BranchPage({ orgInfo, branchInfo, siteInfo }) {
    const { t, i18n } = useTranslation();
    const recipient = useRef("");
    const classes = useStyles();
    const [displayUnits, setDisplayUnits] = useState();
    const [csvReportData, setCSVReportData] = useState([])
    const [pdfReportData, setPDFReportData] = useState([]);
    const [pdfReportDataHeaders, setPDFReportDataHeaders] = useState([]);
    const [pdfReportConfigHeaders, setPDFReportConfigHeaders] = useState([]);
    const [csvFileName, setCSVFileName] = useState('reports.csv');
    const [pdfFileName, setPDFFileName] = useState('reports');
    const csvLink = useRef();
    const csvHeaders = [
        { label: t("site"), key: 'SiteName' },
        { label: t("tank"), key: 'name' },
        { label: t("status"), key: 'Status' },
        { label: t("fluid"), key: 'fluidName' },
        { label: t("ullage"), key: 'ullage_disp' },
        { label: t("lastReading"), key: 'ReadingTime_CSV' },
        { label: t("city"), key: 'City' },
        { label: t("inventory"), key: 'volume_disp' },
        { label: t("inventoryPercent"), key: 'volume_pct' },
        { label: t("dte"), key: 'DTE' },
        { label: t("adu"), key: 'ADU_disp' },
        { label: t("capacity"), key: 'FillCapacity_disp' },
        { label: t("fluidHeight"), key: 'fluid_height_disp' },
        { label: t("nextReading"), key: 'NextReading_CSV' },
        { label: t("rssi"), key: 'rssi' },
        { label: t("batt"), key: 'batt' },
        { label: t("lastFill"), key: 'FillTime_CSV' },
        { label: t("lastFillV"), key: "FillVolume_disp"},
        { label: t("efficiency"), key: 'Efficiency' },
        { label: t("serialNumber"), key: 'UID'},
        { label: t("family"), key: 'Family'},
        { label: t("latitude"), key: 'latitude' },
        { label: t("longitude"), key: 'longitude' },
    ];
    const pdfHeaders = [
        { label: t("site"), key: 'SiteName' },
        { label: t("tank"), key: 'name' },
        { label: t("status"), key: 'Status' },
        { label: t("fluid"), key: 'fluidName' },
        { label: t("lastReading"), key: 'ReadingTime_PDF' },
        { label: t("ullage"), key: 'ullage_disp' },
        { label: t("inventory"), key: 'volume_disp' },
        { label: t("dte"), key: 'DTE' },
    ];

    // Default columns for the Branches table.
    const [siteNameColWidth, setSiteNameColWidth] = React.useState(128);
    const [tankColWidth, setTankColWidth] = React.useState(104);
    const [fluidColWidth, setfluidColWidth] = React.useState(96);
    const [cityColWidth, setCityColWidth] = React.useState(168);
    const [sitePinWidth, setSitePinWidth] = React.useState(64);
    const defaultColumnOrder =[
        'coordinates',
        'SiteName', 
        'name', 
        'Status', 
        'fluidName', 
        'ullage_disp_str',
        'ReadingTime_UTC',
        'City',
        'volume_disp_str',
        'volume_pct',
        'DTE_disp',
        'ADU_disp_str',
        'Efficiency',
        'FillCapacity_disp_str',
        'fluid_height_disp_str',
        'rssi',
        'batt',
        'FillTime',
        'FillVolume_disp_str',
        'NextReading',
    ];
    const [columnOrder, setColumnOrder] = useState([]);
    const [checked2, setChecked2] = useState({
        'coordinates':true, 
        'SiteName':true, 
        'name':true, 
        'Status':true, 
        'fluidName':true, 
        'ullage_disp_str':true,
        'ReadingTime_UTC':true,
        'City':false,
        'volume_disp_str':false,
        'volume_pct':false,
        'DTE_disp':false,
        'ADU_disp_str':false,
        'Efficiency':false,
        'FillCapacity_disp_str':false,
        'fluid_height_disp_str':false,
        'rssi':false,
        'batt':false,
        'FillTime':false,
        'FillVolume_disp_str':false,
        'NextReading':false,
    });
    const columnsAlwaysOn = ['name', 'Status', 'coordinates'];
    const columnsBranch = [
        {
            headerName: t("pin"),
            field: 'coordinates',
            width: sitePinWidth,
            renderCell: renderPin,
            headerClassName: classes.tableHeader,
            sortable: false,
            disableClickEventBubbling: true
        },
        {
            headerName: t("site"),
            field: 'SiteName',
            width: siteNameColWidth,
            renderCell: renderLink,
            headerClassName: classes.tableHeader,
            disableClickEventBubbling: true
        },
        {
            headerName: t("tank"),
            field: 'name',
            width: tankColWidth,
            renderCell: renderLink,
            headerClassName: classes.tableHeader,
            disableClickEventBubbling: true
        },
        {
            headerName: t("status"),
            field: 'Status',
            width: 104,
            renderCell: renderStatus,
            headerClassName: classes.tableHeader,
            sortComparator: (v1, v2, param1, param2) => param1.row.status_severity - param2.row.status_severity,
            disableClickEventBubbling: true
        },
        {
            headerName: t("fluid"),
            field: 'fluidName',
            width: fluidColWidth,
            headerClassName: classes.tableHeader,
            disableClickEventBubbling: true
        },
        {
            headerName: t("ullage"),
            field: 'ullage_disp_str',
            sortComparator: (v1, v2, param1, param2) => param1.row.ullage_disp - param2.row.ullage_disp,
            width: 112,
            headerClassName: classes.tableHeader,
            disableClickEventBubbling: true
        },
        {
            headerName: t("lastReading"),
            field: 'ReadingTime_UTC',
            width: 190,
            type: 'dateTime',
            headerClassName: classes.tableHeader,
            disableClickEventBubbling: true
        },
        {
            headerName: t("city"),
            field: 'City',
            width: cityColWidth,
            headerClassName: classes.tableHeader,
            disableClickEventBubbling: true
        },
        {
            headerName: t("Inv"),
            description: t("inventory"),
            field: 'volume_disp_str',
            sortComparator: (v1, v2, param1, param2) => param1.row.volume_disp - param2.row.volume_disp,
            headerClassName: classes.tableHeader,
            disableClickEventBubbling: true
        },
        {
            headerName: t("InvPercent"),
            description: t("inventory"),
            field: 'volume_pct',
            sortComparator: (v1,v2, param1, param2) => param1.row.volume_pct_int - param2.row.volume_pct_int,
            valueFormatter: (params) => {
                const valueFormatted = params.value === null ? "" : params.value;
                const unit = (params.value === undefined || params.value === null) ? "" : "%"
                return `${valueFormatted}${unit}`;
            },
            headerClassName: classes.tableHeader,
            disableClickEventBubbling: true
        },
        {
            headerName: t("dte"),
            description: t("DaysToEmpty"),
            field: 'DTE_disp',
            headerClassName: classes.tableHeader,
            disableClickEventBubbling: true
        },
        {
            headerName: t("adu"),
            description: t("averageDailyUsage"),
            field: 'ADU_disp_str',
            sortComparator: (v1, v2, param1, param2) => param1.row.ADU_disp - param2.row.ADU_disp,
            headerClassName: classes.tableHeader,
            disableClickEventBubbling: true
        },
        {
            headerName: t("fe"),
            description: t("fillEfficiency"),
            field: 'Efficiency',
            valueFormatter: (params) => {
                const valueFormatted = params.value === null ? "" : params.value;
                const unit = (params.value === undefined || params.value === null) ? "" : "%"
                return `${valueFormatted}${unit}`;
            },
            headerClassName: classes.tableHeader,
            disableClickEventBubbling: true
        },
        {
            headerName: t("cap"),
            description: t("Capacity"),
            field: 'FillCapacity_disp_str',
            sortComparator: (v1, v2, param1, param2) => param1.row.FillCapacity_disp - param2.row.FillCapacity_disp,
            headerClassName: classes.tableHeader,
            disableClickEventBubbling: true
        },
        {
            headerName: t("fluidHt"),
            field: 'fluid_height_disp_str',
            sortComparator: (v1, v2, param1, param2) => param1.row.fluid_height_disp - param2.row.fluid_height_disp,
            headerClassName: classes.tableHeader,
            disableClickEventBubbling: true
        },
        {
            headerName: t("rssi"),
            description: t("rfSignalStrength"),
            field: 'rssi',
            valueFormatter: (params) => {
                const valueFormatted = params.value === null ? "" : params.value;
                const unit = (params.value === undefined || params.value === null) ? "" : "%"
                return `${valueFormatted}${unit}`;
            },
            headerClassName: classes.tableHeader,
            disableClickEventBubbling: true
        },
        {
            headerName: t("batt"),
            description: t("battery"),
            field: 'batt',
            valueFormatter: (params) => {
                const valueFormatted = params.value === null ? "" : params.value;
                const unit = (params.value === undefined || params.value === null) ? "" : "%"
                return `${valueFormatted}${unit}`;
            },
            headerClassName: classes.tableHeader,
            disableClickEventBubbling: true
        },
        {
            headerName: t("lastFill"),
            field: 'FillTime',
            type: 'dateTime',
            width: 190,
            headerClassName: classes.tableHeader,
            disableClickEventBubbling: true
        },
        {
            headerName: t("fv"),
            description: t("lastFillVolume"),
            field: 'FillVolume_disp_str',
            sortComparator: (v1, v2, param1, param2) => param1.row.FillVolume_sort - param2.row.FillVolume_sort,
            headerClassName: classes.tableHeader,
            disableClickEventBubbling: true
        },
        {
            headerName: t("nextReading"),
            field: 'NextReading',
            type: 'dateTime',
            width: 190,
            headerClassName: classes.tableHeader,
            disableClickEventBubbling: true
        },
    ];
    const handleSetChecked2 = (newChecked) => {
        if (useCookiesForData) {
            var cookies = new Cookies();
            cookies.set(COOKIE_NAMES.branchColumnsChecked.replace("{orgName}", orgInfo.orgName), JSON.stringify(newChecked), { maxAge: 1000000 });
        }
        else {
            updateUserSettingsData(COOKIE_NAMES.branchColumnsChecked.replace("{orgName}", orgInfo.orgName), JSON.stringify(newChecked));
        }
        setChecked2(newChecked);
    };
    const handleSetColumnOrder = (newOrder) => {
        if (useCookiesForData) {
            var cookies = new Cookies();
            cookies.set(COOKIE_NAMES.branchColumnsOrder.replace("{orgName}", orgInfo.orgName), JSON.stringify(newOrder), { maxAge: 1000000 });
        }
        else {
            updateUserSettingsData(COOKIE_NAMES.branchColumnsOrder.replace("{orgName}", orgInfo.orgName),JSON.stringify(newOrder));
        }
        setColumnOrder(newOrder);
    };
    
    // Create state for the invites
    const [inviteOpen, setInviteOpen] = React.useState(false);
    // Create state for the Google Map.
    const [mapOpen, setMapOpen] = React.useState(true);
    const [mapPins, setMapPins] = React.useState([]);
    const [mapCenter, setMapCenter] = React.useState(getCenter());
    // Create state for the data table.
    const [dataReady, setDataReady] = React.useState(0);
    const [tankInfo, setSiteInfo] = React.useState({});
    const [fluids, setFluids] = React.useState([]);
    const [tableCols, setTableCols] = React.useState([]);
    const [tableRows, setTableRows] = React.useState([]);
    const [reportsMenuItems, setReportsMenuItems] = React.useState({});
    const [filtersInfo, setFiltersInfo] = React.useState({ 'fluidName': [], 'statusName': [], 'tagName': [], 'ullageRange': [], 'DTE': "noFilter", 'showSensorWarning': 0 });
    const [currentSearchTerm, setCurrentSearchTerm] = React.useState("");

    function isValidEnteredEmail(value) {
        if (isValidEmail(value))
            return (true);

        alert(t("youHaveEnteredAnInvalidEmail"));
        return (false);
    }

    function calculateStatusSeverity(status, showSensorWarning) {
        var severity = 1;
        if ((typeof status) !== "string") {
            severity = status_severity.unknown;
        }
        else if (status.includes("Comm")) {
            severity = status_severity.Comm;
        }
        else if (status.includes("Batt")) {
            severity = status_severity.Batt;
        }
        else if (status.includes("Crit")) {
            severity = status_severity.Crit;
        }
        else if (status.includes("SensErr")){
            severity = status_severity.SensE;
        }
        else if (status.includes("High")) {
            severity = status_severity.High;
        }
        else if (status.includes("Low")) {
            severity = status_severity.Low;
        }
        else if (status.includes("SensW") && showSensorWarning) {
            severity = status_severity.SensW;
        }
        else if (status.includes("None")) {
            severity = status_severity.unknown;
        }
        return severity;
    }

    function createCSVFileName(fileNamePrefix) {
        let d = new Date;
        let dformat = `${("0" + d.getDate()).slice(-2)}${("0" + (d.getMonth() + 1)).slice(-2)}${d.getFullYear()}_${d.getHours()}-${d.getMinutes()}-${d.getSeconds()}`;

        setCSVFileName(fileNamePrefix + '_' + t('snapshot') + '_' + dformat + '.csv');
        setPDFFileName(fileNamePrefix + '_' + t('snapshot') + '_' + dformat);
    };

    function createCSVData(data_in) {
        //console.log(data_in);
        var i;
        var k;
        var csvData = [[t("organizationName"), orgInfo.orgName]];
        csvData.push([t("branchName"), branchInfo.branchName]);

        let d = new Date;
        csvData.push([t("dateCreated"), convertDatetoCSVFormat(d)]);

        if (displayUnits.includes("Metric")) {
            csvData.push([t("volumeUnits"), t("liters_caps")]);
            csvData.push([t("dimensionsUnits"), t("centimeters")]);
        } else {
            csvData.push([t("volumeUnits"), t("gallons_caps")]);
            csvData.push([t("dimensionsUnits"), t("inches")]);
        }

        csvData.push(['']);
        var tempRow = [];

        for (i = 0; i < csvHeaders.length; i++) {
            tempRow.push(csvHeaders[i].label);
        }

        csvData.push(tempRow);
        if (data_in.length > 0) {
            for (k = 0; k < data_in.length; k++) {
                tempRow = []
                for (i = 0; i < csvHeaders.length; i++) {
                    if (data_in[k][csvHeaders[i].key] === null || data_in[k][csvHeaders[i].key] === undefined) {
                        tempRow.push("");
                    }
                    else {
                        if (csvHeaders[i].key === "UID"){
                            tempRow.push("\'{uid}".replace("{uid}", data_in[k][csvHeaders[i].key]));
                        } else{
                            tempRow.push(data_in[k][csvHeaders[i].key]);
                        }
                    }
                }
                csvData.push(tempRow);
            }
        }
        //console.log(csvData);
        setCSVReportData(csvData);
    };

    function convertUTCDateToLocalDate(date) {
        var newDate = new Date(date.getTime() + date.getTimezoneOffset() * 60 * 1000);

        var offset = date.getTimezoneOffset() / 60;
        var hours = date.getHours();

        newDate.setHours(hours - offset);

        var month = '' + (newDate.getMonth() + 1),
            day = '' + newDate.getDate(),
            year = newDate.getFullYear(),
            hour = newDate.getHours(),
            minute = newDate.getMinutes(),
            second = newDate.getSeconds();

        if (month.length < 2)
            month = '0' + month;

        if (day.length < 2)
            day = '0' + day;

        if (minute < 10)
            minute = '0' + minute;

        if (hour < 10)
            hour = '0' + hour;

        var prettiestStr = month + '/' + day + '/' + year + ' ' + hour + ':' + minute;
        return prettiestStr;
    };

    function createPDFData(data_in) {
        var headerArr = [];
        var headerConfigArr = [];
        var bodyArr = [];
        var i;
        var k;
        let d = new Date;

        headerConfigArr.push([t("organizationName"), orgInfo.orgName]);
        headerConfigArr.push([t("branchName"), branchInfo.branchName]);
        headerConfigArr.push([t("dateCreated"), d]);

        if (displayUnits.includes("Metric")) {
            headerConfigArr.push([t("volumeUnits"), t("liters_caps")]);
            headerConfigArr.push([t("dimensionsUnits"), t("centimeters")])
        } else {
            headerConfigArr.push([t("volumeUnits"), t("gallons_caps")]);
            headerConfigArr.push([t("dimensionsUnits"), t("inches")]);
        }

        setPDFReportConfigHeaders(headerConfigArr);

        if (data_in.length > 0) {
            for (i = 0; i < pdfHeaders.length; i++) {
                headerArr.push(pdfHeaders[i].label);
            }

            for (k = 0; k < data_in.length; k++) {
                var entryArray = [];
                for (i = 0; i < pdfHeaders.length; i++) {
                    if (data_in[k][pdfHeaders[i].key] === null || data_in[k][pdfHeaders[i].key] === undefined) {
                        entryArray.push("");
                    }
                    else {
                        entryArray.push(data_in[k][pdfHeaders[i].key]);
                    }
                }
                bodyArr.push(entryArray);
            }
        }

        setPDFReportDataHeaders([headerArr]);
        setPDFReportData(bodyArr);
    };

    function convertDatetoCSVFormat(inputTimeStamp) {
        //Excel based on 1990.  Java based on 1970.
        let ret = inputTimeStamp;
        //CGI:  Don't convert the time if it is not a number.
        if (!isNaN(inputTimeStamp.getTime())) {
            ret = inputTimeStamp.toISOString().slice(0, 19).replace('T', ' ').replace(/-/g, '/');
            ret = convertUTCDateToLocalDate(new Date(ret));
        }

        return ret;
    }

    function createTableData(data, showSensorWarning=0) {
        var data_in = data;
        const vol_string = displayUnits.includes("US") ? "gal" : "L";
        const dim_string = displayUnits.includes("US") ? "in" : "cm";
        
        if (displayUnits.includes("Metric")) {
            for (var i = 0; i < data_in.length; i++) {
                data_in[i].status_severity = calculateStatusSeverity(data_in[i].Status, showSensorWarning);
                data_in[i].showSensorWarning = showSensorWarning;
                if (!isNaN(data_in[i].fluid_height)) {
                    if (data_in[i].DimensionUnit === 'inches') {
                        data_in[i].fluid_height_disp = Math.round(data_in[i].fluid_height * 2.54);
                    }
                    else if (data_in[i].DimensionUnit === 'feet') {
                        data_in[i].fluid_height_disp = Math.round(data_in[i].fluid_height * 2.54 * 12.0);
                    }
                    else if (data_in[i].DimensionUnit === 'centimeters') {
                        data_in[i].fluid_height_disp = Math.round(data_in[i].fluid_height);
                    }
                }

                if (data_in[i].VolumeUnit === 'gallons') {
                    if (!isNaN(data_in[i].ullage)) {
                        data_in[i].ullage_disp = Math.round(data_in[i].ullage * 3.78541);
                    }
                    if (!isNaN(data_in[i].volume)) {
                        data_in[i].volume_disp = Math.round(data_in[i].volume * 3.78541);
                    }
                    if (!isNaN(data_in[i].FillCapacity)) {
                        data_in[i].FillCapacity_disp = Math.round(data_in[i].FillCapacity * 3.78541);
                    }
                    if (!isNaN(data_in[i].ADU)) {
                        data_in[i].ADU_disp = roundToDecimals(data_in[i].ADU * 3.78541, 2);
                    }
                    if (!isNaN(data_in[i].FillVolume)){
                        data_in[i].FillVolume_disp = Math.round(data_in[i].FillVolume * 3.78541);
                    }
                }
                else if (data_in[i].VolumeUnit === "liters") {
                    if (!isNaN(data_in[i].ullage)) {
                        data_in[i].ullage_disp = Math.round(data_in[i].ullage);
                    }
                    if (!isNaN(data_in[i].volume)) {
                        data_in[i].volume_disp = Math.round(data_in[i].volume);
                    }
                    if (!isNaN(data_in[i].FillCapacity)) {
                        data_in[i].FillCapacity_disp = Math.round(data_in[i].FillCapacity);
                    }
                    if (!isNaN(data_in[i].ADU)) {
                        data_in[i].ADU_disp = roundToDecimals(data_in[i].ADU, 2);
                    }
                    if (!isNaN(data_in[i].FillVolume)){
                        data_in[i].FillVolume_disp = Math.round(data_in[i].FillVolume);
                    }
                }

                if (!isNaN(data_in[i].ullage_disp)) {
                    data_in[i].ullage_disp_str = data_in[i].ullage_disp.toFixed(0).toString() + ' ' + vol_string;
                }
                if (!isNaN(data_in[i].volume_disp)) {
                    data_in[i].volume_disp_str = data_in[i].volume_disp.toFixed(0).toString() + ' ' + vol_string;
                }
                if (!isNaN(data_in[i].FillCapacity_disp)) {
                    data_in[i].FillCapacity_disp_str = data_in[i].FillCapacity_disp.toFixed(0).toString() + ' ' + vol_string;
                }
                if (!isNaN(data_in[i].ADU_disp)) {
                    data_in[i].ADU_disp_str = data_in[i].ADU_disp.toFixed(2).toString() + ' ' + vol_string;
                }
                if (!isNaN(data_in[i].fluid_height_disp)) {
                    data_in[i].fluid_height_disp_str = data_in[i].fluid_height_disp.toFixed(0).toString() + ' ' + dim_string;
                }
                if (!isNaN(data_in[i].FillVolume_disp)) {
                    data_in[i].FillVolume_disp_str = data_in[i].FillVolume_disp.toFixed(0).toString() + ' ' + vol_string;
                    data_in[i].FillVolume_sort = data_in[i].FillVolume_disp;
                }
                else {
                    data_in[i].FillVolume_sort = -1;
                }
            }
        }
        else {
            for (var i = 0; i < data_in.length; i++) {
                data_in[i].status_severity = calculateStatusSeverity(data_in[i].Status, showSensorWarning);
                data_in[i].showSensorWarning = showSensorWarning;
                if (!isNaN(data_in[i].fluid_height)) {
                    if (data_in[i].DimensionUnit === 'inches') {
                        data_in[i].fluid_height_disp = Math.round(data_in[i].fluid_height);
                    }
                    else if (data_in[i].DimensionUnit === 'feet') {
                        data_in[i].fluid_height_disp = Math.round(data_in[i].fluid_height * 12.0);
                    }
                    else if (data_in[i].DimensionUnit === 'centimeters') {
                        data_in[i].fluid_height_disp = Math.round(data_in[i].fluid_height / 2.54);
                    }
                }

                if (data_in[i].VolumeUnit === 'gallons') {
                    if (!isNaN(data_in[i].ullage)) {
                        data_in[i].ullage_disp = Math.round(data_in[i].ullage);
                    }
                    if (!isNaN(data_in[i].volume)) {
                        data_in[i].volume_disp = Math.round(data_in[i].volume);
                    }
                    if (!isNaN(data_in[i].ADU)) {
                        data_in[i].ADU_disp = roundToDecimals(data_in[i].ADU, 2);
                    }
                    if (!isNaN(data_in[i].FillCapacity)) {
                        data_in[i].FillCapacity_disp = Math.round(data_in[i].FillCapacity);
                    }
                    if (!isNaN(data_in[i].FillVolume)) {
                        data_in[i].FillVolume_disp = Math.round(data_in[i].FillVolume);
                    }
                }
                else if (data_in[i].VolumeUnit === "liters") {
                    if (!isNaN(data_in[i].ullage)) {
                        data_in[i].ullage_disp = Math.round(data_in[i].ullage / 3.78541);
                    }
                    if (!isNaN(data_in[i].volume)) {
                        data_in[i].volume_disp = Math.round(data_in[i].volume / 3.78541);
                    }
                    if (!isNaN(data_in[i].ADU)) {
                        data_in[i].ADU_disp = roundToDecimals(data_in[i].ADU / 3.78541, 2);
                    }
                    if (!isNaN(data_in[i].FillCapacity)) {
                        data_in[i].FillCapacity_disp = Math.round(data_in[i].FillCapacity / 3.78541);
                    }
                    if (!isNaN(data_in[i].FillVolume)) {
                        data_in[i].FillVolume_disp = Math.round(data_in[i].FillVolume) / 3.78541;
                    }
                }

                if (!isNaN(data_in[i].ullage_disp)) {
                    data_in[i].ullage_disp_str = data_in[i].ullage_disp.toFixed(0).toString() + ' ' + vol_string;
                }
                if (!isNaN(data_in[i].volume_disp)) {
                    data_in[i].volume_disp_str = data_in[i].volume_disp.toFixed(0).toString() + ' ' + vol_string;
                }
                if (!isNaN(data_in[i].FillCapacity_disp)) {
                    data_in[i].FillCapacity_disp_str = data_in[i].FillCapacity_disp.toFixed(0).toString() + ' ' + vol_string;
                }
                if (!isNaN(data_in[i].ADU_disp)) {
                    data_in[i].ADU_disp_str = data_in[i].ADU_disp.toFixed(2).toString() + ' ' + vol_string;
                }
                if (!isNaN(data_in[i].fluid_height_disp)) {
                    data_in[i].fluid_height_disp_str = data_in[i].fluid_height_disp.toFixed(0).toString() + ' ' + dim_string;
                }
                if (!isNaN(data_in[i].FillVolume_disp)) {
                    data_in[i].FillVolume_disp_str = data_in[i].FillVolume_disp.toFixed(0).toString() + ' ' + vol_string;
                    data_in[i].FillVolume_sort = data_in[i].FillVolume_disp;
                }
                else {
                    data_in[i].FillVolume_sort = -1;
                }
            }
        }

        return data_in;
    };

    React.useEffect(() => {
        if (tankInfo.json_data !== undefined) {
            FilterData([], false);
        }

        const tCol = tableCols;
        setTableCols(tCol);
    }, [displayUnits, tankInfo, currentSearchTerm]);

    React.useEffect(() => {
        var t_tableCols = tableCols;
        for (var i = 0; i < tableCols.length; i++) {
            if (t_tableCols[i].field === "SiteName") {
                t_tableCols[i].width = siteNameColWidth;
            }
            if (t_tableCols[i].field === 'fluidName') {
                t_tableCols[i].width = fluidColWidth;
            }
            if (t_tableCols[i].field === 'name') {
                t_tableCols[i].width = tankColWidth;
            }
            if (t_tableCols[i].field === 'City') {
                t_tableCols[i].width = cityColWidth;
            }
            if (t_tableCols[i].field === 'coordinates') {
                t_tableCols[i].width = sitePinWidth;
            }
        }
        setTableCols(t_tableCols);
    }, [siteNameColWidth, fluidColWidth, tankColWidth, cityColWidth, sitePinWidth])

    // Setup the data table. Triggers when the site info changes.
    function fetchSiteData(){
        var cookies = new Cookies();
        var tFilter = cookies.get('branchFilter' + branchInfo.branchId);

        if (undefined !== tFilter) {
            setFiltersInfo(tFilter);
        }

        var tDisplayUnits = cookies.get('DisplayUnits');
        if (undefined !== tDisplayUnits) {
            setDisplayUnits(tDisplayUnits);
        }
        else {
            setDisplayUnits("US");
        }
        if (useCookiesForData){
            var tChecked2 = cookies.get(COOKIE_NAMES.branchColumnsChecked.replace("{orgName}", orgInfo.orgName));
            if (undefined !== tChecked2){
                setChecked2(tChecked2);
            }
        }

        var tMapOpen = cookies.get('mapOpen');
        if (undefined !== tMapOpen) {
            setMapOpen(tMapOpen === 'false' ? false : true);
        }

        Auth.currentAuthenticatedUser()
            .then(data => {
                console.log("Fetching branch, site readings...")

                var requestOptions = {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': data.signInUserSession.idToken.jwtToken
                    }
                };

                fetch(host + '/orgs/' + orgInfo.orgId + '/branches/' + branchInfo.branchId + '/sites', requestOptions)
                    .then(results => results.json())
                    .then(data => {
                        console.log("Branch, site readings fetched.")

                        var Data = data.data;
                        Data.json_data = data.data.json_data.sort((a, b) => (parseFloat(a.ullage_pct) > parseFloat(b.ullage_pct)) ? -1 : ((parseFloat(b.ullage_pct) > parseFloat(a.ullage_pct)) ? 1 : 0));

                        var f = [];
                        var maxSiteNameLength = 16;
                        var maxTankNameLength = 13;
                        var maxFluidNameLength = 12;
                        var maxCityNameLength = 16;
                        var tankNavigation = 0;

                        for (var i = 0; i < Data.json_data.length; i++) {
                            try {
                                if (Data.json_data[i].SiteName.length > maxSiteNameLength) {
                                    maxSiteNameLength = Data.json_data[i].SiteName.length;
                                }
                            } catch (error) { }
                            try {
                                if (Data.json_data[i].fluidName.length > maxFluidNameLength) {
                                    maxFluidNameLength = Data.json_data[i].fluidName.length;
                                }
                            } catch (error) { }
                            try {
                                if (Data.json_data[i].name.length > maxTankNameLength) {
                                    maxTankNameLength = Data.json_data[i].name.length;
                                }
                            } catch (error) { }
                            try {
                                if (Data.json_data[i].City.length > maxCityNameLength) {
                                    maxCityNameLength = Data.json_data[i].City.length;
                                }
                            } catch (error) { }
                            try {
                                if (Data.json_data[i].gps_enable) {
                                    tankNavigation += 1;
                                }
                            } catch (error) { }
                            try {
                                Data.json_data[i].volume_pct_int = parseInt(Data.json_data[i].volume_pct);
                            } catch (error) {
                                Data.json_data[i].volume_pct_int = undefined;
                            }
                            Data.json_data[i].id = [orgInfo.orgName, branchInfo.branchName, Data.json_data[i].SiteName, Data.json_data[i].name].join(",");
                            if (Data.json_data[i].ReadingTime === null) {
                                delete Data.json_data[i].ReadingTime_UTC;
                            }

                            Data.json_data[i].ReadingTime_UTC = new Date(Data.json_data[i].ReadingTime_UTC);
                            Data.json_data[i].ReadingTime_CSV = convertDatetoCSVFormat((Data.json_data[i].ReadingTime_UTC));
                            Data.json_data[i].ReadingTime_PDF = Data.json_data[i].ReadingTime_UTC.toLocaleString();
                            Data.json_data[i].NextReading_CSV = "";

                            if (Data.json_data[i].NextReading) {
                                Data.json_data[i].NextReading = new Date(Data.json_data[i].NextReading);
                                Data.json_data[i].NextReading_CSV = convertDatetoCSVFormat((Data.json_data[i].NextReading));
                            }

                            Data.json_data[i].FillTime_CSV = ""
                            if (Data.json_data[i].FillTime) {
                                Data.json_data[i].FillTime = new Date(Data.json_data[i].FillTime);
                                Data.json_data[i].FillTime_CSV = convertDatetoCSVFormat(Data.json_data[i].FillTime);
                            }

                            Data.json_data[i].ullage = parseFloat(Data.json_data[i].ullage);
                            Data.json_data[i].volume = parseFloat(Data.json_data[i].volume);
                            Data.json_data[i].FillCapacity = parseFloat(Data.json_data[i].FillCapacity);
                            Data.json_data[i].FillVolume = parseFloat(Data.json_data[i].FillVolume);
                            Data.json_data[i].ADU = parseFloat(Data.json_data[i].ADU);
                            Data.json_data[i].fluid_height = parseFloat(Data.json_data[i].fluid_height);
                            if (Data.json_data[i].DTE !== undefined && Data.json_data[i].DTE !== null) {
                                Data.json_data[i].DTE_disp = parseFloat(Data.json_data[i].DTE);
                            }

                            if ((typeof Data.json_data[i].Status) !== "string") {
                                Data.json_data[i].Status = "None";
                            }
                            
                            Data.json_data[i].status_severity = calculateStatusSeverity(Data.json_data[i].Status, 0);
                        }
                        //console.log(Data.json_data);

                        for (var i = 0; i < Data.fluidsList.length; i++) {
                            f.push({ 'id': i, 'name': Data.fluidsList[i] })
                        }
                        setSiteNameColWidth(maxSiteNameLength * 10 + 8);
                        setfluidColWidth(maxFluidNameLength * 9 + 8);
                        setTankColWidth(maxTankNameLength * 9 + 8);
                        setCityColWidth(maxCityNameLength * 9 + 8);
                        if (tankNavigation) {
                            setSitePinWidth(80);
                        }
                        else {
                            setSitePinWidth(64);
                        }
                        setFluids(f);
                        setSiteInfo(Data);
                        setDataReady(1);
                        createCSVFileName(branchInfo.branchName);
                    }).catch((error) => {
                        console.log(error);
                    });
            })
    };

    async function fetchUserSettingsData(){
        try {
            let tempOrder = [];
            var i;
            var tempUpdates = {};
            
            const { status, data } = await userSettingsData.getUserSettingsData();

            const error = data.error;
            const settingsData = data.data;
            
            if (status === 404) {
                console.log("fetchUserSettingsData 404");
            }

            if (error !== 'OK' && status !== 200) {
                alert(t("sorryUnableToRetrieve"));
                throw new Error(error);
            }

            //console.log(settingsData);
            var cookies = new Cookies();
            //Code to check for cookies and use existing cookies (update)
            if (settingsData[COOKIE_NAMES.branchColumnsOrder.replace("{orgName}",orgInfo.orgName)]) {
                setColumnOrder(JSON.parse(settingsData[COOKIE_NAMES.branchColumnsOrder.replace("{orgName}",orgInfo.orgName)]));
            }
            else if (cookies.get(COOKIE_NAMES.branchColumnsOrder.replace("{orgName}",orgInfo.orgName))) {
                //Use and remove cookie
                console.log("Cookies Detected for Column Order");
                var tColumnOrder = cookies.get(COOKIE_NAMES.branchColumnsOrder.replace("{orgName}",orgInfo.orgName));

                if (undefined !== tColumnOrder){
                    setColumnOrder(tColumnOrder);
                }
                else {
                    defaultColumnOrder.forEach(function(value, index) {
                        for(i=0; i < columnsBranch.length; i++){
                            if (columnsBranch[i].field === value){
                                tempOrder.push({key:value, label: FieldSelectLabel[value]});
                                break;
                            }
                        }
                    });
                }
                tempUpdates[COOKIE_NAMES.branchColumnsOrder.replace("{orgName}",orgInfo.orgName)] = JSON.stringify(tColumnOrder);
            }
            else {
                //Defaults
                console.log("Load Default Column Order");
                defaultColumnOrder.forEach(function(value, index) {
                    for(i=0; i < columnsBranch.length; i++){
                        if (columnsBranch[i].field === value){
                            tempOrder.push({key:value, label: FieldSelectLabel[value]});
                            break;
                        }
                    }
                });
                setColumnOrder(tempOrder);
            }

            //Checked Columns
            if (settingsData[COOKIE_NAMES.branchColumnsChecked.replace("{orgName}", orgInfo.orgName)]){
                setChecked2(JSON.parse(settingsData[COOKIE_NAMES.branchColumnsChecked.replace("{orgName}", orgInfo.orgName)]))
            }
            else if(cookies.get(COOKIE_NAMES.branchColumnsChecked.replace("{orgName}", orgInfo.orgName))){
                console.log("Cookies Detected for columns checked");
                var tChecked2 = cookies.get(COOKIE_NAMES.branchColumnsChecked.replace("{orgName}", orgInfo.orgName));
                if (undefined !== tChecked2){
                    setChecked2(tChecked2);
                }
                tempUpdates[COOKIE_NAMES.branchColumnsChecked.replace("{orgName}", orgInfo.orgName)] = JSON.stringify(tChecked2);
            }
            else {
                //Default
            }
            
            if(Object.keys(tempUpdates).length){
                const { status, data } = await userSettingsData.updateUserSettingsData(tempUpdates);
                if (status === 200) {
                    console.log("Removing old cookies");
                    cookies.remove(COOKIE_NAMES.branchColumnsOrder.replace("{orgName}",orgInfo.orgName));
                    cookies.remove(COOKIE_NAMES.branchColumnsChecked.replace("{orgName}", orgInfo.orgName));
                }
                else {
                    console.log("Failed to update back-end with cookie data")
                }
            }
        } catch (error) {
            console.error(error);
        }
    }

    async function updateUserSettingsData(settingsName, settingsDataIn){
        const lsettingsName = settingsName;
        const lsettingsDataIn = settingsDataIn;
        try {
            var updateData;
            
            updateData = userSettingsData.formatSettings(lsettingsName, lsettingsDataIn);
            const { status, data } = await userSettingsData.updateUserSettingsData(updateData);
            const { error } = data;
            const settingsData = data.data;

            if (status === 404) {
                console.log("updateUserSettingsData 404");
            }

            if (error !== 'OK' && status !== 200) {
                alert(t("sorryUnableToRetrieve"));
                throw new Error(error);
            }

            //console.log(settingsData);
            
        } catch (error) {
            console.error(error);
        }
    }

    React.useEffect(() => {
        if (!useCookiesForData){
            fetchUserSettingsData();
        }
        fetchSiteData();
    }, [siteInfo]);

    // Update the map. Triggers when the table is refreshed.
    useEffect(() => {
        setMapCenter({ lat: getCenter().lat, lng: getCenter().lng });
        setMapPins(makePins());
    }, [tableRows]);

    // Update the table column display. Triggers when the column list changes.
    useEffect(() => {
        let newColumns = [];
        var i, k;
        if (columnOrder.length){
            columnOrder.forEach(function (item, index) {
                for (i=0; i < columnsBranch.length; i++){
                    if (columnsBranch[i].field === item.key){
                        if (checked2[item.key]){
                            newColumns.push(columnsBranch[i]);
                        }
                        break;
                    }
                }
            });
            setTableCols(newColumns);
        }
    }, [checked2, columnOrder]);

    useEffect(() => {
        let tempOrder = []
        var cookies = new Cookies();
        var i;
        
        if (columnOrder.length == 0 && useCookiesForData){
            var tColumnOrder = cookies.get(COOKIE_NAMES.branchColumnsOrder.replace("{orgName}",orgInfo.orgName));
            tColumnOrder = undefined;
            if (undefined !== tColumnOrder){
                setColumnOrder(tColumnOrder);
            }
            else {
                console.log("Set default Column Order");
                defaultColumnOrder.forEach(function(value, index) {
                for(i=0; i < columnsBranch.length; i++){
                    if (columnsBranch[i].field === value){
                        tempOrder.push({key:value, label: FieldSelectLabel[value]});
                        break;
                    }
                }
                });
                setColumnOrder(tempOrder);
            }
        }
    }, []);

    // Functions to handle UI
    const handleInviteOpen = () => {
        setInviteOpen(true);
    };

    const handleInviteClose = () => {
        setInviteOpen(false);
    };

    const handleMapToggle = () => {
        var cookies = new Cookies();
        cookies.set('mapOpen', !mapOpen, { path: "/", maxAge: 1000000 });
        setMapOpen(!mapOpen);
    };

    function updateDisplayUnits(input_units) {
        var cookies = new Cookies();
        cookies.set('DisplayUnits', input_units, { path: "/", maxAge: 1000000 });
        setDisplayUnits(input_units);
    }

    // Render functions to help properly render the table.
    function renderPin(params) {
        //console.log(params.row);
        //console.log(params.row.tankGps);
        var tank_latitude = 0;
        var tank_longitude = 0;
        var isValid = false;
        var gps_enable = parseInt(params.row.gps_enable);
        if (isNaN(gps_enable)){
            gps_enable = 0;
        }
        if (params.row.tankGps) {
            tank_latitude = params.row.tankGps.latitude;
            tank_longitude = params.row.tankGps.longitude;
            if (tank_latitude !== undefined || tank_longitude !== undefined){
                isValid = true;
            }
        }
        //let url = "https://google.com/maps/place/" + tank_latitude + "," + tank_longitude;
        let url = "https://maps.google.com/?q=" + tank_latitude + "," + tank_longitude;
        //console.log(url);
        return (
            <Grid item xs>
                <IconButton size='small' onClick={() => setMapCenter({ lat: params.row.latitude, lng: params.row.longitude })}>
                    <Place color='primary' />
                </IconButton>
                {(gps_enable > 0) && <Tooltip title={isValid ? t('tankLocation') : t('invalidTankLocation')} placement='right'>
                    <IconButton size='small' aria-haspopup='true'
                            disabled={!isValid}
                            color='inherit'
                            onClick={() => window.open(url)}>
                        <NavigationIcon />
                    </IconButton>
                </Tooltip>}
            </Grid>

        );
    }

    function renderLink(params) {
        const orgName = (params.row.id.split(","))[0];
        const branchName = (params.row.id.split(","))[1];
        return (
            <Link className={classes.link}
                component={RouterLink}
                to={params.field === 'SiteName'
                    ? '/orgs/' + orgName + '/branches/' + branchName + '/sites/' + params.value
                    : '/orgs/' + orgName + '/branches/' + branchName + '/sites/' + params.getValue('SiteName') + '/tanks/' + params.value}>
                {params.value}
            </Link>
        );
    }

    function renderStatus(params) {
        var status = params.value;
        var status_key = "ok"
        var status_disp = t("ok");

        if ((typeof status) !== "string") {
            status_disp = "";
            status_key = "";
        }
        else if (status.includes("Comm")) {
            status_disp = t("comm");
            status_key = "comm";
        }
        else if (status.includes("SensErr")){
            status_disp = t("senserr");
            status_key = "sensorError";
        }
        else if (status.includes("Crit")) {
            status_disp = t("crit");
            status_key = "crit";
        }
        else if (status.includes("Batt")) {
            status_disp = t("batt");
            status_key = "batt";
        }
        else if (status.includes("High")) {
            status_disp = t("high");
            status_key = "high";
        }
        else if (status.includes("Low")) {
            status_disp = t("low");
            status_key = "low";
        }
        else if (status.includes("SensW") && params.row.showSensorWarning){
            status_disp = t("sensw");
            status_key = "sensorWarning"
        }
        else if (status.includes("None")) {
            status_disp = t("none");
            status_key = "none"
        }
        var param_display = "";
        if (status !== undefined) {
            var param_display_list = status.toLowerCase().split(",");
            for (var i = 0; i < param_display_list.length; i++) {
                param_display_list[i] = t(param_display_list[i]);
            }
            param_display = param_display_list.join(",");
        }
        return (
            <Tooltip title={params.value !== undefined ? param_display : ""}>
                <Chip label={status_disp} size="small"
                    className={status_key === "ok"
                        ? classes.ok
                        : status_key === "low"
                            ? classes.low
                            : status_key === "high"
                                ? classes.low
                                : status_key === "crit"
                                    ? classes.crit
                                    : status_key === "sensorWarning"
                                    ? classes.low
                                        : status_key === "none" ? classes.statusNone : classes.error} />
            </Tooltip>
        );
    }
    
    function FilterData(filterData, update = false) {
        var addItem = false;
        var newData = [];
        var tstatus = [];
        var ullage_pct = 0;
        var filters = {};
        var data_DTE = 0;

        if (update) {
            var cookies = new Cookies();
            setFiltersInfo(filterData);
            cookies.set("branchFilter" + branchInfo.branchId, JSON.stringify(filterData), {path: "/", maxAge: 1000000 });
            filters = filterData;
        }
        else {
            filters = filtersInfo;
        }

        for (var i = 0; i < tankInfo.json_data.length; i++) {
            addItem = true;
            if (filters.fluidName.length && addItem) {
                for (var j = 0; j < filters.fluidName.length; j++) {
                    if (-1 == filters.fluidName[j].indexOf(tankInfo.json_data[i].fluidName)) {
                        addItem = false;
                    }
                    else {
                        addItem = true;
                        break;
                    }
                }
            }
            if (filters.tagName.length && addItem) {
                addItem = false;
                if ('Tags' in tankInfo.json_data[i]) {
                    for (var j = 0; j < tankInfo.json_data[i].Tags.length; j++) {
                        for (var k = 0; k < filters.tagName.length; k++) {
                            if (-1 !== filters.tagName[k].indexOf(tankInfo.json_data[i].Tags[j])) {
                                addItem = true;
                                break;
                            }
                        }
                        if (addItem) { break; }
                    }
                }
            }
            if (filters.statusName.length && addItem) {
                tstatus = tankInfo.json_data[i].Status.split(",");
                addItem = false;
                for (var j = 0; j < tstatus.length; j++) {
                    for (var k = 0; k < filters.statusName.length; k++) {
                        if (-1 !== filters.statusName[k].indexOf(tstatus[j])) {
                            addItem = true;
                            break;
                        }
                    }
                    if (addItem) { break; }
                }
            }
            if (filters.DTE && (filters.DTE !== "noFilter") && addItem) {
                data_DTE = (tankInfo.json_data[i].DTE);
                if (data_DTE >= parseFloat(filters.DTE) || null === data_DTE || undefined === data_DTE) {
                    addItem = false;
                }
            }
            if ((filters.ullageRange[0] > 0 || filters.ullageRange[1] < 100) && addItem) {
                ullage_pct = parseFloat(tankInfo.json_data[i].ullage_pct);
                if (ullage_pct < filters.ullageRange[0] || ullage_pct > filters.ullageRange[1]) {
                    addItem = false;
                }
            }
            if (currentSearchTerm){
                if(tankInfo.json_data[i].name){
                    let tankName = tankInfo.json_data[i].name.toUpperCase();
                    let lSearchTerm = currentSearchTerm.toUpperCase();
                    if(!tankName.includes(lSearchTerm)){
                        addItem = false;
                    }
                }
                else{
                    addItem = false;
                }
            }
            if (addItem) {
                newData.push(tankInfo.json_data[i]);
            }
            else {
                console.log(tankInfo.json_data[i]);
            }
        }
        createCSVFileName(branchInfo.branchName);
        newData = createTableData(newData, filters.showSensorWarning);
        setTableRows(newData);
        createCSVData(newData);
        createPDFData(newData);
    }
    
    function makePins() {
        var pins = [];
        var recorded = [];
        var row;
        var pinIndex;

        if (tableRows === undefined)
            return pins;

        for (row = 0; row < tableRows.length; row += 1) {
            var site = tableRows[row]['SiteName'];
            var status = tableRows[row].Status;

            if (!recorded.includes(site)) {
                recorded.push(site);
                pins.push(tableRows[row]);
            }
            else if (status !== 'OK' || status !== undefined) {
                pinIndex = pins.findIndex((el) => { return el['SiteName'] === site; });

                if (status === 'Comm')
                    pins.splice(pinIndex, 1, tableRows[row]);
                else if (status === 'Crit' && pins[pinIndex]['Status'] !== 'Comm')
                    pins.splice(pinIndex, 1, tableRows[row]);
                else if (pins[pinIndex]['Status'] === 'OK')
                    pins.splice(pinIndex, 1, tableRows[row]);
            }
        }
        return pins;
    }

    function getCenter() {
        try {
            var row;
            var latMin = tableRows[0]['latitude'];
            var latMax = tableRows[0]['latitude'];
            var lngMin = tableRows[0]['longitude'];
            var lngMax = tableRows[0]['longitude'];

            for (row = 1; row < tableRows.length; row += 1) {
                if (tableRows[row]['latitude'] < latMin)
                    latMin = tableRows[row]['latitude'];
                else if (tableRows[row]['latitude'] > latMax)
                    latMax = tableRows[row]['latitude'];
                if (tableRows[row]['longitude'] < lngMin)
                    lngMin = tableRows[row]['longitude'];
                else if (tableRows[row]['longitude'] > lngMax)
                    lngMax = tableRows[row]['longitude'];
            }

            return {
                lat: (latMax + latMin) / 2,
                lng: (lngMax + lngMin) / 2
            };
        } catch (e) {
            return {
                // Default is Graco!
                lat: 44.9778,
                lng: -93.2650
            };
        }
    }

    // Functions to handle invitations
    const handleInviteAsViewer = async () => {
        var recip = recipient.current.value.trim().replace(/,{1,}$/, '');
        recip = recip.trim();
        var recipientArr = recip.split(',');
        var sent = false;

        for (var index = 0; index < recipientArr.length; ++index) {
            var userToInvite = recipientArr[index];

            if (isValidEnteredEmail(userToInvite)) {
                try {
                    // Get current authenticated user session
                    let user = await Auth.currentAuthenticatedUser();
                    user.getSession((err, session) => {
                        if (err) {
                            console.log(err);
                            return;
                        }

                        setInviteOpen(false);

                        var body = JSON.stringify(
                            {
                                "Recipient": userToInvite,
                                "Id": branchInfo.branchId,
                                "Role": "Viewer",
                                'User': user.attributes.email
                            }
                        );

                        var requestOptions = {
                            method: 'POST',
                            headers: {
                                'Content-Type': 'application/json',
                                'Authorization': session.getIdToken().jwtToken
                            },
                            body: body
                        };

                        fetch(host + '/orgs/' + orgInfo.orgId + '/branches/' + branchInfo.branchId + '/invite', requestOptions)
                            .then(async response => {
                                const data = await response.json();
                                if (response.status == 200) {
                                    if (!sent) {
                                        alert(t("invitationHasBeenSent"));
                                        sent = true;
                                    }
                                }
                                else {
                                    alert(data);
                                }
                            });
                    });
                } catch (e) {
                    console.log(e);
                }
            }
        }
    };

    const handleInviteAsEditor = async () => {
        var recip = recipient.current.value.trim().replace(/,{1,}$/, '');
        recip = recip.trim();
        var recipientArr = recip.split(',');
        var sent = false;

        for (var index = 0; index < recipientArr.length; ++index) {
            var userToInvite = recipientArr[index];

            if (isValidEnteredEmail(userToInvite)) {
                try {
                    // Get current authenticated user session
                    let user = await Auth.currentAuthenticatedUser();
                    user.getSession((err, session) => {
                        if (err) {
                            console.log(err);
                            return;
                        }

                        setInviteOpen(false);

                        var body = JSON.stringify(
                            {
                                "Recipient": userToInvite,
                                "Id": branchInfo.branchId,
                                "Role": "Editor",
                                'User': user.attributes.email
                            }
                        );

                        var requestOptions = {
                            method: 'POST',
                            headers: {
                                'Content-Type': 'application/json',
                                'Authorization': session.getIdToken().jwtToken
                            },
                            body: body
                        };

                        fetch(host + '/orgs/' + orgInfo.orgId + '/branches/' + branchInfo.branchId + '/invite', requestOptions)
                            .then(async response => {
                                const data = await response.json();
                                if (response.status == 200) {
                                    if (!sent) {
                                        alert(t("invitationHasBeenSent"));
                                        sent = true;
                                    }
                                }
                                else {
                                    alert(data);
                                }
                            });
                    });
                } catch (e) {
                    console.log(e);
                }
            }
        }
    };

    const downloadCSVReport = (event) => {
        console.log("Downloading CSV for branch data...");
        csvLink.current.link.click();
    };

    const downloadPDFReport = (event) => {
        console.log("Downloading PDF for branch data...");

        const pdf = new jsPDF('l', 'pt', 'a4');

        var img = new Image();
        img.src = 'data:image/png;base64,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';
        pdf.addImage(img, 'png', 40, 0, 240, 50);

        pdf.autoTable({ html: '#report-data-table' });
        pdf.autoTable({
            body: pdfReportConfigHeaders,
            startY: 50,
        });

        pdf.autoTable({ html: '#report-table' });
        pdf.autoTable({
            head: pdfReportDataHeaders,
            body: pdfReportData,
        });

        pdf.save(pdfFileName);
    };

    // POST data to API to schedule a report
    const scheduleRecurringReport = async (showing, weekly, recurrTime, state, recurrMonthlyDay, recurrMonthlyWeek, recurrUnits, recurringInput) => {
        try {
            let user = await Auth.currentAuthenticatedUser();
            user.getSession((err, session) => {
                if (err) {
                    console.log(err);
                    return;
                }
                // console.log(recurringInput);
                const sessionToken = session.getIdToken().jwtToken;

                // Adjust client's time to UTC
                var clientTimeZoneOffset = new Date().getTimezoneOffset() / 60;
                var adjustedReportTime = parseInt(recurrTime) + clientTimeZoneOffset;

                if (adjustedReportTime < 0) {
                    adjustedReportTime = adjustedReportTime + 24;
                }
                if (adjustedReportTime > 23) {
                    adjustedReportTime = adjustedReportTime - 24;
                }

                var now = new Date();
                var then = new Date();

                now.setHours(parseInt(recurrTime), 0, 0, 0);
                then.setHours(parseInt(recurrTime), 0, 0, 0);
                var n = now.getTimezoneOffset() / 60;
                then.setHours(then.getHours() + n);
                var timeOffset = 0;

                if (now.getDate() < then.getDate()) {
                    // console.log("UTC time conversion results in days to be shifted forward one ...");
                    timeOffset = 1;

                    if (recurringInput == "FirstOfMonth") {
                        recurringInput = "SecondOfMonth"
                    }

                    if (weekly) {
                        var tuesdayUpdated = false;
                        var wednesdayUpdated = false;
                        var thursdaydayUpdated = false;
                        var fridaydayUpdated = false;
                        var saturdayUpdated = false;
                        var sundayUpdated = false;

                        var stateCopy = JSON.parse(JSON.stringify(state));

                        if (stateCopy['monday']) {
                            state['monday'] = false;
                            state['tuesday'] = true;
                            tuesdayUpdated = true;
                        }
                        if (stateCopy['tuesday']) {
                            if (!tuesdayUpdated)
                                state['tuesday'] = false;
                            state['wednesday'] = true;
                            wednesdayUpdated = true;
                        }
                        if (stateCopy['wednesday']) {
                            if (!wednesdayUpdated)
                                state['wednesday'] = false;
                            state['thursday'] = true;
                            thursdaydayUpdated = true;
                        }
                        if (stateCopy['thursday']) {
                            if (!thursdaydayUpdated)
                                state['thursday'] = false;
                            state['friday'] = true;
                            fridaydayUpdated = true;
                        }
                        if (stateCopy['friday']) {
                            if (!fridaydayUpdated)
                                state['friday'] = false;
                            state['saturday'] = true;
                            saturdayUpdated = true;
                        }
                        if (stateCopy['saturday']) {
                            if (!saturdayUpdated)
                                state['saturday'] = false;
                            state['sunday'] = true;
                            sundayUpdated = true;
                        }
                        if (stateCopy['sunday']) {
                            if (!sundayUpdated)
                                state['sunday'] = false;
                            state['monday'] = true;
                        }
                    } else if (!weekly && showing) {
                        switch (recurrMonthlyDay) {
                            case 'Sunday':
                                recurrMonthlyDay = 'Monday';
                                break;
                            case 'Monday':
                                recurrMonthlyDay = 'Tuesday';
                                break;
                            case 'Tuesday':
                                recurrMonthlyDay = 'Wednesday';
                                break;
                            case 'Wednesday':
                                recurrMonthlyDay = 'Thursday';
                                break;
                            case 'Thursday':
                                recurrMonthlyDay = 'Friday';
                                break;
                            case 'Friday':
                                recurrMonthlyDay = 'Saturday';
                                break;
                            case 'Saturday':
                                recurrMonthlyDay = 'Sunday';
                                break;
                            default:
                        }
                    }
                } else if (now.getDate() > then.getDate()) {
                    // console.log("UTC time conversion results in days to be shifted back one ...");
                    timeOffset = -1;

                    if (recurringInput == "FirstOfMonth") {
                        recurringInput = "LastOfMonth"
                    }

                    if (weekly) {
                        var tuesdayUpdated = false;
                        var wednesdayUpdated = false;
                        var thursdaydayUpdated = false;
                        var fridaydayUpdated = false;
                        var saturdayUpdated = false;
                        var mondayUpdated = false;

                        var stateCopy = JSON.parse(JSON.stringify(state));

                        if (stateCopy['sunday']) {
                            state['sunday'] = false;
                            state['saturday'] = true;
                            saturdayUpdated = true;
                        }

                        if (stateCopy['saturday']) {
                            if (!saturdayUpdated)
                                state['saturday'] = false;
                            state['friday'] = true;
                            fridaydayUpdated = true;
                        }

                        if (stateCopy['friday']) {
                            if (!fridaydayUpdated)
                                state['friday'] = false;
                            state['thursday'] = true;
                            thursdaydayUpdated = true;
                        }

                        if (stateCopy['thursday']) {
                            if (!thursdaydayUpdated)
                                state['thursday'] = false;
                            state['wednesday'] = true;
                            wednesdayUpdated = true;
                        }

                        if (stateCopy['wednesday']) {
                            if (!wednesdayUpdated)
                                state['wednesday'] = false;
                            state['tuesday'] = true;
                            tuesdayUpdated = true;
                        }

                        if (stateCopy['tuesday']) {
                            if (!tuesdayUpdated)
                                state['tuesday'] = false;
                            state['monday'] = true;
                            mondayUpdated = false;
                        }

                        if (stateCopy['monday']) {
                            if (!mondayUpdated)
                                state['monday'] = false;
                            state['sunday'] = true;
                        }
                    } else if (!weekly && showing) {
                        switch (recurrMonthlyDay) {
                            case 'Sunday':
                                recurrMonthlyDay = 'Saturday';
                                break;
                            case 'Monday':
                                recurrMonthlyDay = 'Sunday';
                                break;
                            case 'Tuesday':
                                recurrMonthlyDay = 'Monday';
                                break;
                            case 'Wednesday':
                                recurrMonthlyDay = 'Tuesday';
                                break;
                            case 'Thursday':
                                recurrMonthlyDay = 'Wednesday';
                                break;
                            case 'Friday':
                                recurrMonthlyDay = 'Thursday';
                                break;
                            case 'Saturday':
                                recurrMonthlyDay = 'Friday';
                                break;
                            default:
                        }
                    }
                } else {
                    // No day shifting required ...
                }
                // console.log(recurringInput);
                var body = JSON.stringify(
                    {
                        "RecurStatus": recurringInput,
                        "Weekly": weekly,
                        "Monthly": !weekly && showing,
                        "OrgName": orgInfo.orgName,
                        "RecurrTime": adjustedReportTime,
                        "RecurrDays": state,
                        "MonthlyDay": recurrMonthlyDay,
                        "MonthlyWeek": recurrMonthlyWeek,
                        "Units": recurrUnits,
                        "Shift": timeOffset,
                        "TimeZoneAdjust": clientTimeZoneOffset,
                    }
                );

                var requestOptions = {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': sessionToken
                    },
                    body: body
                };

                fetch(host + '/orgs/' + orgInfo.orgId + '/branches/' + branchInfo.branchId + '/reports', requestOptions)
                    .then(response => response.json())
                    .then(data => {
                        // console.log(data);
                    });
            });
        } catch (e) {
            console.log(e);
        }
    };

    // GET data from API to schedule a report
    const getUserRecurringReportRequest = async (event) => {
        let user = await Auth.currentAuthenticatedUser()
            .then(data => {
                console.log("Fetching branch, user reports schedule...")

                var requestOptions = {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': data.signInUserSession.idToken.jwtToken
                    }
                };

                fetch(host + '/orgs/' + orgInfo.orgId + '/branches/' + branchInfo.branchId + '/reports', requestOptions)
                    .then(results => results.json())
                    .then(data => {
                        setReportsMenuItems(data.body);
                    }).catch((error) => {
                        // console.log(error);
                    });
            });
    };

    // DELETE scheduled report
    const deleteRecurrSchedule = async (event) => {
        let user = await Auth.currentAuthenticatedUser()
            .then(data => {
                console.log("Deleting branch, user reports schedule...")

                var requestOptions = {
                    method: 'DELETE',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': data.signInUserSession.idToken.jwtToken
                    }
                };

                fetch(host + '/orgs/' + orgInfo.orgId + '/branches/' + branchInfo.branchId + '/reports', requestOptions)
                    .then(results => results.json())
                    .then(data => {
                        console.log(data);
                    }).catch((error) => {
                        console.log(error);
                    });
            });
    };
    //console.log(tableRows);

    if (!dataReady) {
        return (<Grid container 
                className={classes.content}
                direction='column'
                alignItems='center'
                justifyContent='center'> 
            <Grid item xs> <Typography>{t("loadingSiteData")}</Typography></Grid>
            <div/>
            <Grid item xs> <CircularProgress className={classes.preLoader} /> </Grid>
        </Grid>);
    }

    const getGoogleMap = (isOnTop) =>
        <GoogleMap isOnTop={isOnTop} org={orgInfo.orgName} branch={branchInfo.branchName} pins={mapPins} center={mapCenter} />;

    return (
        <Grid container>
            <CSVLink
                data={csvReportData}
                filename={csvFileName}
                className='hidden'
                ref={csvLink}
                target='_blank' />
            <Grid item xs>
                <Toolbar>
                    <div className={classes.wrap}>
                        <TlmBreadcrumbs edge='start' orgInfo={orgInfo} branchInfo={branchInfo} site={null} tank={null} />
                        <Hidden smDown>
                            <NavigationButton text={t("sites_caps")} siteInfo={siteInfo} orgInfo={orgInfo} branchName={branchInfo.branchName} isModuleEditor={branchInfo.isBranchEditor}>
                            </NavigationButton>
                        </Hidden>
                    </div>
                    <Hidden ><div className={classes.grow_2x} /></Hidden>

                    <div className={classes.wrap}>
                        {branchInfo.isBranchEditor ?
                            <>
                                <Hidden smDown>
                                    <IconButton aria-haspopup='true' color='inherit' className={classes.iconButtons} onClick={handleInviteOpen}>
                                        <PersonAdd />
                                        <Typography className={classes.HeaderIcon}>{t("invite")}</Typography>
                                    </IconButton>
                                </Hidden>

                            </> :
                            <div className={classes.grow} />
                        }
                        <Hidden smDown>
                            <IconButton edge='end' className={classes.iconButtons} onClick={handleMapToggle} color='inherit'>
                                <Map />
                                <Typography className={classes.HeaderIcon}>{mapOpen ? t('hideMap') : t("showMap")}</Typography>
                            </IconButton>
                        </Hidden>

                    </div>
                </Toolbar>
                <Hidden mdUp>
                    <Toolbar>
                        <Hidden mdUp>
                            <NavigationButton text={t("sites_caps")} siteInfo={siteInfo} orgInfo={orgInfo} branchName={branchInfo.branchName} isModuleEditor={branchInfo.isBranchEditor}>
                            </NavigationButton>
                        </Hidden>
                        {branchInfo.isBranchEditor &&
                            <>
                                <Hidden smDown>
                                    <IconButton aria-haspopup='true' color='inherit' className={classes.iconButtons} onClick={handleInviteOpen}>
                                        <PersonAdd />
                                        <Typography className={classes.HeaderIcon}>{t("invite")}</Typography>
                                    </IconButton>
                                </Hidden>
                                <Hidden mdUp>
                                    <Tooltip title={t("invite")}>
                                        <IconButton aria-haspopup='true' color='inherit' className={classes.iconButtons} onClick={handleInviteOpen}>
                                            <PersonAdd />
                                        </IconButton>
                                    </Tooltip>
                                </Hidden>
                            </>}
                        <Hidden mdUp>
                            <Tooltip title={mapOpen ? t('hideMap') : t("showMap")}>
                                <IconButton edge='end' className={classes.iconButtons} onClick={handleMapToggle} color='inherit'>
                                    <Map />
                                </IconButton>
                            </Tooltip>
                        </Hidden>
                    </Toolbar>
                </Hidden>
                <Divider />
                <Dialog open={inviteOpen} onClose={handleInviteClose} aria-labelledby="form-dialog-title">
                    <DialogTitle id="form-dialog-title">{t("invite")}</DialogTitle>
                    <DialogContent>
                        <DialogContentText>
                            {t("toInviteAUserPleaseEnter")}
                        </DialogContentText>
                        <TextField
                            autoFocus
                            margin="dense"
                            id="name"
                            inputRef={recipient}
                            label={t("emailAddress")}
                            type="email"
                            fullWidth />
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleInviteClose} color="primary">
                            {t("cancel")}
                        </Button>
                        <Button onClick={handleInviteAsViewer} variant="contained" color="primary">
                            {t('asViewer')}
                        </Button>
                        <Button onClick={handleInviteAsEditor} variant="contained" color="primary">
                            {t("asEditor")}
                        </Button>
                    </DialogActions>
                </Dialog>
                {mapOpen &&
                    <Hidden mdUp>
                        <Grid item xs>
                            <div className={classes.topMap}>
                                {getGoogleMap(true)}
                            </div>
                        </Grid>
                    </Hidden>
                }
                <TlmFilterBar
                    showRefresh={true}
                    refreshFunction={fetchSiteData}
                    tags={tankInfo.tagsList}
                    fluids={fluids}
                    columnOrder={columnOrder} checked2={checked2} setChecked2={handleSetChecked2} setOrder={handleSetColumnOrder}
                    disableColumns={columnsAlwaysOn}
                    setFilters={FilterData}
                    filtersInfo={filtersInfo}
                    currentSearchTerm={currentSearchTerm}
                    handleSearchTerm={(value) => {setCurrentSearchTerm(value);}}
                    displayUnits={displayUnits}
                    setDisplayUnits={updateDisplayUnits}
                    onCSVDownloadRequest={downloadCSVReport}
                    onPDFDownloadRequest={downloadPDFReport}
                    onRecurringReportRequest={scheduleRecurringReport}
                    onGetUserRecurringReportRequest={getUserRecurringReportRequest}
                    deleteRecurrentReportRequest={deleteRecurrSchedule}
                    reportingMenuItems={reportsMenuItems}
                    disableScheduleReport={!branchInfo.isBranchViewer} />
                {mapOpen ?
                    <div className={classes.table}>
                        <DataGrid
                            hideFooterSelectedRowCount
                            rowsPerPageOptions={[25, 50, 100]}
                            pagination
                            pageSize={100}
                            rows={tableRows}
                            columns={tableCols} />
                    </div>
                    :<div className={classes.table_noMap}>
                        <DataGrid
                            hideFooterSelectedRowCount
                            rowsPerPageOptions={[25, 50, 100]}
                            pagination
                            pageSize={100}
                            rows={tableRows}
                            columns={tableCols} />
                    </div>
                }
            </Grid>
            {mapOpen &&
                <Hidden smDown>
                    <Grid item md={6}>
                        {getGoogleMap(false)}
                    </Grid>
                </Hidden>
            }
        </Grid>
    );
}